/**
 * URL locale utilities for handling language prefixes like /ar/, /fr/
 * Built to work alongside existing query param system for smooth transition
 */

import { languages } from "@stylingcv/utils";

import { defaultLocale } from "../libs/lingui";

/**
 * Extract locale from URL path
 */
export function getLocaleFromPath(pathname: string): string | null {
  const segment = pathname.split("/").find(Boolean);
  const firstSegment = segment;

  if (!firstSegment) return null;

  // Check if first segment matches a supported locale (check both id and locale)
  const found = languages.find(
    (lang) =>
      lang.locale.toLowerCase() === firstSegment.toLowerCase() ||
      lang.id.toLowerCase() === firstSegment.toLowerCase(),
  );

  return found?.locale || null;
}

/**
 * Remove locale prefix from path
 */
export function removeLocaleFromPath(pathname: string): string {
  const localeInPath = getLocaleFromPath(pathname);
  if (!localeInPath) return pathname;

  const segments = pathname.split("/").filter(Boolean);

  // Only remove if the first segment is indeed the locale we found
  const firstSegment = segments[0];
  if (
    firstSegment &&
    languages.some(
      (lang) =>
        lang.locale.toLowerCase() === firstSegment.toLowerCase() ||
        lang.id.toLowerCase() === firstSegment.toLowerCase(),
    )
  ) {
    segments.shift();
  }

  return "/" + segments.join("/");
}

/**
 * Add locale prefix to path
 */
export function addLocaleToPath(pathname: string, locale: string): string {
  // Don't add prefix for default locale
  if (locale === defaultLocale) {
    return removeLocaleFromPath(pathname);
  }

  const cleanPath = removeLocaleFromPath(pathname);
  return `/${locale}${cleanPath}`;
}

/**
 * Get current locale from URL (path takes priority over query)
 */
export function getCurrentUrlLocale(): string {
  // First try path-based locale
  const pathLocale = getLocaleFromPath(window.location.pathname);
  if (pathLocale) return pathLocale;

  // Fallback to query param
  const searchParams = new URLSearchParams(window.location.search);
  const queryLocale = searchParams.get("locale");
  if (queryLocale) {
    const found = languages.find(
      (lang) =>
        lang.locale.toLowerCase() === queryLocale.toLowerCase() ||
        lang.id.toLowerCase() === queryLocale.toLowerCase(),
    );
    if (found) return found.locale;
  }

  // Fallback to localStorage (set when user changes language)
  if (typeof window !== "undefined") {
    const storedLocale = window.localStorage.getItem("locale");
    if (storedLocale) {
      const found = languages.find(
        (lang) =>
          lang.locale.toLowerCase() === storedLocale.toLowerCase() ||
          lang.id.toLowerCase() === storedLocale.toLowerCase(),
      );
      if (found) return found.locale;
    }
  }

  return defaultLocale;
}

/**
 * Navigate to new locale URL
 * Note: This is used by changeLanguage() - prefer useLocalizedNavigation hook in components
 */
export function navigateToLocale(newLocale: string): void {
  const currentPath = removeLocaleFromPath(window.location.pathname);
  const newPath = addLocaleToPath(currentPath, newLocale);

  console.log(`[URLLocale] Navigating from ${window.location.pathname} to ${newPath}`);

  // Use location.replace for immediate effect without history stack
  window.location.replace(newPath + window.location.search + window.location.hash);
}

/**
 * Check if current URL has locale prefix
 */
export function hasLocalePrefix(): boolean {
  return getLocaleFromPath(window.location.pathname) !== null;
}

/**
 * Create localized navigation helper for React components
 */
export function createLocalizedNavigation(currentPath: string) {
  const currentLocale = getLocaleFromPath(currentPath);

  return {
    currentLocale,

    /**
     * Get localized href for Link components
     */
    getLocalizedHref: (path: string): string => {
      return currentLocale ? addLocaleToPath(path, currentLocale) : path;
    },

    /**
     * Get localized path for navigate() calls
     */
    getLocalizedPath: (path: string): string => {
      return currentLocale ? addLocaleToPath(path, currentLocale) : path;
    },
  };
}
